import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  {
    icon: 'fa-solid fa-server',
    title: '1. Cloud Infrastructure & Architecture',
    desc: 'Design, deploy, and manage high-availability cloud environments across AWS, Azure, and Google Cloud with optimized performance and cost controls.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '2. Network Design & Engineering',
    desc: 'Build secure, enterprise-grade network topologies, SD-WAN solutions, VPNs, and firewall systems to ensure uninterrupted connectivity.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '3. Data Center Virtualization',
    desc: 'Modernize legacy physical infrastructure using VMware, Hyper-V, and containerized architectures for maximum compute density and resource efficiency.',
  },
  {
    icon: 'fa-solid fa-infinity',
    title: '4. DevOps & Infrastructure as Code (IaC)',
    desc: 'Automate server provisioning, continuous integration, and deployments using Terraform, Ansible, Docker, and Kubernetes.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '5. Cyber Security Infrastructure',
    desc: 'Implement zero-trust security architectures, intrusion prevention systems (IPS/IDS), identity management, and end-to-end data encryption.',
  },
  {
    icon: 'fa-solid fa-rotate',
    title: '6. Disaster Recovery & Continuity',
    desc: 'Ensure 99.99% uptime with robust backup solutions, failover systems, automated disaster recovery testing, and zero-data-loss policies.',
  },
  {
    icon: 'fa-solid fa-desktop',
    title: '7. Virtual Desktop Infrastructure (VDI)',
    desc: 'Enable secure, remote hybrid work environments with centralized virtual desktop deployments and unified endpoint management.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '8. Managed IT Infrastructure Support',
    desc: '24/7/365 proactive monitoring, incident management, patch maintenance, and helpdesk support for your mission-critical hardware and cloud setups.',
  },
  {
    icon: 'fa-solid fa-hard-drive',
    title: '9. Enterprise Storage & SAN/NAS',
    desc: 'Implement scalable block, file, and object storage solutions with high-throughput IOPS to support data-intensive enterprise applications.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: '10. Edge Computing Infrastructure',
    desc: 'Deploy low-latency edge nodes and IoT gateways for real-time processing and localized data management.',
  },
];

const whyChooseUs = [
  'Enterprise-grade reliability and 99.99% uptime',
  'Automated Infrastructure as Code (IaC)',
  'Zero-Trust security & compliance architecture',
  'Multi-cloud and hybrid-cloud capabilities',
  '24/7/365 proactive NOC & SOC monitoring',
  'Cost optimization & cloud expenditure control',
  'Rapid disaster recovery and instant failovers',
  'Seamless legacy migration to modern cloud',
  'Scalable compute and storage design',
  'End-to-end lifecycle management',
];

const processSteps = [
  { step: '01', title: 'Assessment & Audit', desc: 'Evaluate existing server capacity, network health, security vulnerabilities, and workload demands.' },
  { step: '02', title: 'Architecture Design', desc: 'Engineered cloud/hybrid blueprints focused on redundancy, security, scalability, and performance.' },
  { step: '03', title: 'Provisioning & IaC', desc: 'Automate infrastructure setup using code for predictable, repeatable, and secure environments.' },
  { step: '04', title: 'Migration & Integration', desc: 'Seamlessly shift workloads and databases with minimal downtime and continuous data integrity.' },
  { step: '05', title: 'Security Hardening', desc: 'Apply firewalls, zero-trust policies, access controls, and compliance standards.' },
  { step: '06', title: '24/7 Monitoring & Optimization', desc: 'Proactively track performance, auto-scale resources, and maintain infrastructure health continuously.' },
];

const techStack = [
  { category: 'Cloud Platforms', items: ['AWS', 'Microsoft Azure', 'Google Cloud (GCP)', 'DigitalOcean'] },
  { category: 'DevOps & IaC', items: ['Terraform', 'Ansible', 'Docker', 'Kubernetes', 'Helm'] },
  { category: 'Virtualization & Storage', items: ['VMware vSphere', 'Hyper-V', 'Ceph', 'AWS S3', 'NetApp'] },
  { category: 'Monitoring & Security', items: ['Prometheus', 'Grafana', 'Nagios', 'Palo Alto', 'Fortinet', 'Cloudflare'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const InfrastructureDevelopment = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Cloud & IT Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Infrastructure <span className="gradient-text-cyan">Development</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we build resilient, scalable, and high-performance IT environments. From cloud architecture and DevOps automation to enterprise networking, we engineer the digital backbone for modern businesses.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Modernize With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Infrastructure Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Trust <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Infrastructure Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Deployment Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Upgrade Your Systems with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your legacy infrastructure into an agile, automated, and hyper-secure system built for maximum efficiency and growth.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Request an Infrastructure Audit &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InfrastructureDevelopment;